import type { AxiosInstance } from "axios";
import type { AdminEvent, AdminEventRequest } from "../types/adminEvent";
import type { TeacherEventMedia } from "../types/teacher";
import { eventPayload, validateMedia } from "./adminEvents";

export const createAdminEventClient = (client: AxiosInstance) => {
  const path = (nurseryId: string, id?: number) => {
    if (!nurseryId) throw new Error("Select a nursery first.");
    return `nurseries/${encodeURIComponent(nurseryId)}/events${id == null ? "" : `/${id}`}`;
  };
  return {
    list: (
      nurseryId: string,
      signal?: AbortSignal,
    ): Promise<{ success: boolean; events: AdminEvent[] }> =>
      client.get(path(nurseryId), { signal }).then((response) => response.data),
    detail: (
      nurseryId: string,
      id: number,
      signal?: AbortSignal,
    ): Promise<{ success: boolean; event: AdminEvent }> =>
      client
        .get(path(nurseryId, id), { signal })
        .then((response) => response.data),
    create: (
      nurseryId: string,
      data: AdminEventRequest,
      signal?: AbortSignal,
    ): Promise<{ success: boolean; event: AdminEvent }> =>
      client
        .post(path(nurseryId), eventPayload(data), { signal })
        .then((response) => response.data),
    update: (
      nurseryId: string,
      id: number,
      data: Partial<AdminEventRequest>,
      signal?: AbortSignal,
    ): Promise<{ success: boolean; event: AdminEvent }> =>
      client
        .put(path(nurseryId, id), eventPayload(data), { signal })
        .then((response) => response.data),
    remove: (
      nurseryId: string,
      id: number,
      signal?: AbortSignal,
    ): Promise<{ success: boolean }> =>
      client
        .delete(path(nurseryId, id), { signal })
        .then((response) => response.data),
    upload: (
      nurseryId: string,
      id: number,
      files: File[],
      signal?: AbortSignal,
      onUploadProgress?: (progressEvent: {
        loaded: number;
        total?: number;
      }) => void,
    ): Promise<{ success: boolean; media: TeacherEventMedia[] }> => {
      const error = validateMedia(files);
      if (error) throw new Error(error);
      const data = new FormData();
      files.forEach((file) => data.append("media[]", file));
      return client
        .post(`${path(nurseryId, id)}/media`, data, {
          signal,
          headers: { "Content-Type": undefined },
          transformRequest: [(body) => body],
          onUploadProgress,
        })
        .then((response) => response.data);
    },
    removeMedia: (
      nurseryId: string,
      id: number,
      mediaId: number,
      signal?: AbortSignal,
    ): Promise<{ success: boolean }> =>
      client
        .delete(`${path(nurseryId, id)}/media/${mediaId}`, { signal })
        .then((response) => response.data),
  };
};
